<script setup>
import { ref, onMounted } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import { useToast } from 'primevue/usetoast';
import { adminAPI } from '../service/api.js';
import EditUserRolesDialog from './EditUserRolesDialog.vue';

// Hardcoded role list, mirroring api/roles/roles.go::Known. Adding a new
// role on the backend requires updating this array. Cheap to keep in sync
// while the list is short; revisit if it grows beyond a handful.
const KNOWN_ROLES = ['api_client_admin', 'role_admin'];

const toast = useToast();

const users = ref([]);
const loading = ref(false);
const editDialogOpen = ref(false);
const editTarget = ref(null);

const fetch = async () => {
  loading.value = true;
  try {
    const response = await adminAPI.listUsers();
    users.value = response.data || [];
  } catch (e) {
    console.error('listUsers failed:', e);
    toast.add({
      severity: 'error',
      summary: 'Failed to load users',
      detail: e?.response?.data || e?.message || 'Unknown error',
      life: 5000,
    });
  } finally {
    loading.value = false;
  }
};

onMounted(fetch);

const openEdit = (user) => {
  editTarget.value = user;
  editDialogOpen.value = true;
};

const roleSeverity = (role) => {
  // Visually distinguish the meta-power role.
  return role === 'role_admin' ? 'warn' : 'info';
};
</script>

<template>
  <div class="admin-view">
    <div class="view-header">
      <h2>Users &amp; roles</h2>
      <small class="hint">
        Showing every user configured in <code>USERS_JSON</code>. Edit a row to grant or revoke roles.
      </small>
    </div>

    <DataTable
      :value="users"
      :loading="loading"
      data-key="username"
      striped-rows
      size="small"
      class="users-table"
    >
      <template #empty>
        <div class="empty">No users found.</div>
      </template>

      <Column field="username" header="Username">
        <template #body="{ data }">
          <span class="mono">{{ data.username }}</span>
        </template>
      </Column>
      <Column header="Roles">
        <template #body="{ data }">
          <div v-if="data.roles && data.roles.length" class="role-tags">
            <Tag
              v-for="r in data.roles"
              :key="r"
              :value="r"
              :severity="roleSeverity(r)"
            />
          </div>
          <span v-else class="no-roles">(none)</span>
        </template>
      </Column>
      <Column header="Actions">
        <template #body="{ data }">
          <Button
            icon="pi pi-pencil"
            label="Edit roles"
            size="small"
            severity="secondary"
            @click="openEdit(data)"
          />
        </template>
      </Column>
    </DataTable>

    <EditUserRolesDialog
      v-model:visible="editDialogOpen"
      :user="editTarget"
      :known-roles="KNOWN_ROLES"
      @changed="fetch"
    />
  </div>
</template>

<style scoped>
.admin-view {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.view-header {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: left;
}
.view-header h2 {
  margin: 0;
}
.hint {
  color: #777;
}
.users-table {
  background: #fff;
}
.mono {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.85rem;
}
.role-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.no-roles {
  color: #999;
  font-style: italic;
}
.empty {
  padding: 2rem;
  color: #888;
}
</style>
